import * as React from 'react';
import { TShippingForm } from './ShippingForm.types';
import './ShippingForm.styled.scss';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { TShippingFormData, TDeliveryMethod } from '../../Checkout.types';
import { DELIVERY_METHODS } from '../../Checkout.constants';
import { DeliveryMethodSelector } from '../DeliveryMethodSelector/DeliveryMethodSelector';

const shippingSchema = yup
  .object({
    firstName: yup
      .string()
      .required('Imię jest wymagane')
      .min(2, 'Imię musi mieć co najmniej 2 znaki'),
    lastName: yup
      .string()
      .required('Nazwisko jest wymagane')
      .min(2, 'Nazwisko musi mieć co najmniej 2 znaki'),
    email: yup
      .string()
      .required('Email jest wymagany')
      .email('Nieprawidłowy format email'),
    phone: yup
      .string()
      .required('Numer telefonu jest wymagany')
      .matches(/^[0-9]{9}$/, 'Numer telefonu musi mieć 9 cyfr'),
    street: yup
      .string()
      .required('Ulica jest wymagana')
      .min(3, 'Ulica musi mieć co najmniej 3 znaki'),
    buildingNumber: yup
      .string()
      .required('Numer budynku jest wymagany')
      .matches(/^[0-9]+[A-Za-z]?$/, 'Nieprawidłowy format numeru budynku'),
    apartmentNumber: yup.string().optional(),
    city: yup
      .string()
      .required('Miasto jest wymagane')
      .min(2, 'Miasto musi mieć co najmniej 2 znaki'),
    postalCode: yup
      .string()
      .required('Kod pocztowy jest wymagany')
      .matches(
        /^[0-9]{2}-[0-9]{3}$/,
        'Kod pocztowy musi być w formacie XX-XXX'
      ),
    deliveryMethod: yup
      .mixed<TDeliveryMethod>()
      .oneOf(
        ['inpost', 'dpd', 'poczta', 'kurier'],
        'Metoda dostawy jest wymagana'
      )
      .required('Metoda dostawy jest wymagana'),
    deliveryPointId: yup.string().optional(),
    deliveryPointName: yup.string().optional(),
  })
  .test('inpost-required', function (value) {
    if (value?.deliveryMethod === 'inpost' && !value?.deliveryPointId) {
      return this.createError({
        path: 'deliveryPointId',
        message: 'Wybierz paczkomat',
      });
    }
    return true;
  }) as yup.ObjectSchema<TShippingFormData>;

export const ShippingForm: TShippingForm = ({
  initialData,
  onContinue,
  onBack,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<TShippingFormData>({
    resolver: yupResolver(shippingSchema) as Resolver<TShippingFormData>,
    defaultValues: initialData || {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      street: '',
      buildingNumber: '',
      apartmentNumber: '',
      city: '',
      postalCode: '',
      deliveryMethod: 'inpost',
    },
  });

  const selectedDeliveryMethod = watch('deliveryMethod');
  const postalCode = watch('postalCode');
  const selectedDeliveryPoint = watch('deliveryPointName');
  const [inPostDialogOpen, setInPostDialogOpen] = React.useState(false);

  const onSubmit = (data: TShippingFormData) => {
    onContinue(data as TShippingFormData);
  };

  const handleInPostSelect = (pointId: string, pointName: string) => {
    setValue('deliveryPointId', pointId);
    setValue('deliveryPointName', pointName);
  };

  const handleDeliveryMethodChange = (method: TDeliveryMethod) => {
    // Reset delivery point when changing method
    if (method !== 'inpost') {
      // You can add logic here to clear delivery point fields
    }
  };

  return (
    <div className="shipping-form">
      <div className="shipping-form__header">
        <Typography variant="h5" className="shipping-form__title">
          Shipping Information
        </Typography>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="shipping-form__form">
        <div className="shipping-form__section">
          <Typography variant="h6" className="shipping-form__section-title">
            Contact Information
          </Typography>
          <div className="shipping-form__row">
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Imię"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  required
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nazwisko"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  required
                />
              )}
            />
          </div>
          <div className="shipping-form__row">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  required
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Numer telefonu"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message || 'Format: 123456789'}
                  required
                />
              )}
            />
          </div>
        </div>

        <Divider className="shipping-form__divider" />

        <div className="shipping-form__section">
          <Typography variant="h6" className="shipping-form__section-title">
            Address
          </Typography>
          <div className="shipping-form__row">
            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Ulica"
                  fullWidth
                  error={!!errors.street}
                  helperText={errors.street?.message}
                  required
                />
              )}
            />
            <Controller
              name="buildingNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Numer budynku"
                  fullWidth
                  error={!!errors.buildingNumber}
                  helperText={errors.buildingNumber?.message}
                  required
                />
              )}
            />
            <Controller
              name="apartmentNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Numer mieszkania (opcjonalnie)"
                  fullWidth
                  error={!!errors.apartmentNumber}
                  helperText={errors.apartmentNumber?.message}
                />
              )}
            />
          </div>
          <div className="shipping-form__row">
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Miasto"
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  required
                />
              )}
            />
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Kod pocztowy"
                  fullWidth
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message || 'Format: XX-XXX'}
                  required
                />
              )}
            />
          </div>
        </div>

        <Divider className="shipping-form__divider" />

        <div className="shipping-form__section">
          <Typography variant="h6" className="shipping-form__section-title">
            Delivery Method
          </Typography>
          <Controller
            name="deliveryMethod"
            control={control}
            render={({ field }) => (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleDeliveryMethodChange(
                      e.target.value as TDeliveryMethod
                    );
                  }}
                >
                  {DELIVERY_METHODS.map((method) => (
                    <Card
                      key={method.id}
                      className={`shipping-form__delivery-card ${
                        selectedDeliveryMethod === method.id
                          ? 'shipping-form__delivery-card--selected'
                          : ''
                      }`}
                    >
                      <CardContent>
                        <FormControlLabel
                          value={method.id}
                          control={<Radio />}
                          label={
                            <div className="shipping-form__delivery-info">
                              <div className="shipping-form__delivery-header">
                                <Typography variant="h6">
                                  {method.name}
                                </Typography>
                                <Typography variant="h6" color="primary">
                                  {method.price === 0
                                    ? 'Darmowa'
                                    : `${method.price} zł`}
                                </Typography>
                              </div>
                              <Typography variant="body2" color="textSecondary">
                                {method.description}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Szacowany czas: {method.estimatedDays}
                              </Typography>
                            </div>
                          }
                        />
                      </CardContent>
                    </Card>
                  ))}
                </RadioGroup>
                {errors.deliveryMethod && (
                  <Typography variant="caption" color="error">
                    {errors.deliveryMethod.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {selectedDeliveryMethod === 'inpost' && (
            <div className="shipping-form__inpost-section">
              {selectedDeliveryPoint ? (
                <div className="shipping-form__inpost-selected">
                  <Typography variant="body2" color="textSecondary">
                    Wybrany paczkomat:
                  </Typography>
                  <Typography
                    variant="body1"
                    className="shipping-form__inpost-selected-name"
                  >
                    {selectedDeliveryPoint}
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setValue('deliveryPointId', '');
                      setValue('deliveryPointName', '');
                    }}
                    className="shipping-form__inpost-change-button"
                  >
                    Zmień
                  </Button>
                </div>
              ) : (
                <>
                  <div className="shipping-form__inpost-note">
                    <Typography variant="body2" color="textSecondary">
                      Wybierz paczkomat InPost dla dostawy
                    </Typography>
                  </div>
                  <Button
                    variant="outlined"
                    onClick={() => setInPostDialogOpen(true)}
                    className="shipping-form__inpost-button"
                  >
                    Wybierz Paczkomat
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <DeliveryMethodSelector
          open={inPostDialogOpen}
          onClose={() => setInPostDialogOpen(false)}
          onSelect={handleInPostSelect}
          deliveryMethod={selectedDeliveryMethod}
          postalCode={postalCode}
        />

        <div className="shipping-form__actions">
          <Button
            variant="outlined"
            onClick={onBack}
            className="shipping-form__back-button"
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            className="shipping-form__continue-button"
          >
            Continue to Payment
          </Button>
        </div>
      </form>
    </div>
  );
};
